import { ApolloError, useMutation } from '@apollo/client'

import { FieldType, Form, FormFieldInput, FormSaveInput, LocalFile, MutationSaveFormArgs } from '@types'

import { useCreateFile } from '@hooks/file/create-file.hook'

import { MUTATION_SAVE_FORM } from '@libs/apollo/mutations/form.mutation'

interface Data {
  saveForm?: Form
}

interface useSaveFormProps {
  onCompleted?: () => void
}

export const useSaveForm = (
  props?: useSaveFormProps,
): {
  saveForm: (input: FormSaveInput, localFiles: Record<string, LocalFile[]>) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [saveForm, { loading: loadingSave, error, data }] = useMutation<Data, MutationSaveFormArgs>(
    MUTATION_SAVE_FORM,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const { createFile, loading: loadingUpload } = useCreateFile()

  const _uploadFiles = async (localFiles: Record<string, LocalFile[]>): Promise<Record<string, string[]>> => {
    const uploadedFiles: Record<string, string[]> = {}

    for (const [fieldId, files] of Object.entries(localFiles)) {
      const fileIds: string[] = []
      for (const file of files) {
        const result = await createFile(file)
        if (result?.id) fileIds.push(result.id)
      }
      if (fileIds.length > 0) {
        uploadedFiles[fieldId] = fileIds
      }
    }

    return uploadedFiles
  }

  const _updateFieldsWithFiles = (fields: FormFieldInput[], uploadedFiles: Record<string, string[]>) => {
    return fields.map((field) => {
      if (field.type === FieldType.File || field.type === FieldType.Picture) {
        const fileField = field.type === FieldType.File ? field.file : field.picture
        if (fileField && uploadedFiles[fileField.id]) {
          return {
            ...field,
            [field.type === FieldType.File ? 'file' : 'picture']: {
              ...fileField,
              fileIds: [...(fileField.fileIds || []), ...uploadedFiles[fileField.id]],
            },
          }
        }
      }
      return field
    })
  }

  const _saveForm = async (input: FormSaveInput, localFiles: Record<string, LocalFile[]>) => {
    // Upload all local files first
    const uploadedFiles = await _uploadFiles(localFiles)

    const processedInput = structuredClone(input)

    // Update fields with new file IDs
    processedInput.formModel.fields = _updateFieldsWithFiles(input.formModel.fields, uploadedFiles)

    saveForm({
      variables: { input: processedInput },
      onCompleted: props?.onCompleted,
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'Form', id: input.formId }),
          fields: {
            formModel: () => data?.saveForm?.formModel,
          },
        })
      },
    })
  }

  return { saveForm: _saveForm, loading: loadingUpload || loadingSave, error, data }
}
